/**
 * Groups feature — queries + mutations.
 *
 * Backed by internal/tenant/group.AdminHandler, mounted at
 * /api/v1/tenants/{tenant_id}/groups via the
 * WithPathTenant wrapper.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  groupMembersResponseSchema,
  groupSchema,
  groupsListResponseSchema,
  type CreateGroupInput,
  type Group,
  type GroupMember,
  type GroupsListResponse,
  type GroupsListSearch,
  type UpdateGroupInput,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/groups`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchGroups(
  tenantID: string,
  search: GroupsListSearch,
): Promise<GroupsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: {
      q: search.q,
      sort_by: search.sortBy,
      sort_order: search.sortOrder,
      offset: search.offset,
    },
  });
  return groupsListResponseSchema.parse(raw);
}

export async function fetchGroup(
  tenantID: string,
  groupId: string,
): Promise<Group> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(groupId)}`,
  );
  return groupSchema.parse(raw);
}

export async function fetchGroupMembers(
  tenantID: string,
  groupId: string,
): Promise<readonly GroupMember[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(groupId)}/members`,
  );
  return groupMembersResponseSchema.parse(raw).data;
}

// ── Queries ───────────────────────────────────────────────────────────────

export function useGroupsQuery(tenantID: string, search: GroupsListSearch) {
  return useQuery<GroupsListResponse, ApiError>({
    queryKey: queryKeys.groups.list(tenantID, search),
    queryFn: () => fetchGroups(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useGroupQuery(tenantID: string, groupId: string) {
  return useQuery<Group, ApiError>({
    queryKey: queryKeys.groups.detail(tenantID, groupId),
    queryFn: () => fetchGroup(tenantID, groupId),
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

export function useGroupMembersQuery(tenantID: string, groupId: string) {
  return useQuery<readonly GroupMember[], ApiError>({
    queryKey: queryKeys.groups.members(tenantID, groupId),
    queryFn: () => fetchGroupMembers(tenantID, groupId),
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateGroupMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Group, ApiError, CreateGroupInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => groupSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.all(tenantID) });
    },
  });
}

export function useUpdateGroupMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<Group, ApiError, UpdateGroupInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(groupId)}`, {
          method: "PATCH",
          body: input,
        }).then((raw) => groupSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.groups.detail(tenantID, groupId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

export function useDeleteGroupMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (groupId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(groupId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, groupId) => {
      qc.removeQueries({ queryKey: queryKeys.groups.detail(tenantID, groupId) });
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

/**
 * useAddGroupMembersMutation adds one or more users to a group in a
 * single request (POST /members with { user_ids }). The backend adds
 * them atomically and idempotently, so the caller doesn't fan out N
 * calls — one round trip, one step-up ceremony, one audit batch. The
 * mutation input is the (de-duplicated) set of user ids to add.
 */
export function useAddGroupMembersMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, readonly string[]>({
    mutationFn: (userIds) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(groupId)}/members`,
          {
            method: "POST",
            body: { user_ids: userIds },
          },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.groups.members(tenantID, groupId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.groups.detail(tenantID, groupId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

export function useRemoveGroupMemberMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (userId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.groups.members(tenantID, groupId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.groups.detail(tenantID, groupId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}
